import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, parseRupiah, toInputDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";

const PRODUCT_TYPES = ["Kemeja", "Jaket", "Seragam", "Lainnya"];

const emptyItem = () => ({
  product_type: "Kemeja",
  product_name: "",
  quantity: 1,
  unit_price: "",
  notes: "",
  measurement: {
    untuk_siapa: "Umum",
    lebar_ukuran: 0, lebar_punggung: 0, lingkar_dada: 0,
    panjang_lengan: 0, panjang_tangan: 0, panjang_badan: 0,
  },
});

const MEAS_FIELDS = [
  { key: "lebar_ukuran", label: "Lebar Ukuran" },
  { key: "lebar_punggung", label: "Lebar Punggung" },
  { key: "lingkar_dada", label: "Lingkar Dada" },
  { key: "panjang_lengan", label: "Panjang Lengan" },
  { key: "panjang_tangan", label: "Panjang Tangan" },
  { key: "panjang_badan", label: "Panjang Badan" },
];

export default function OrderForm() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectCustomer = searchParams.get("customer") || "";
  const [customers, setCustomers] = useState([]);
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", address: "" });

  const [form, setForm] = useState({
    customer_id: preselectCustomer,
    target_selesai: "",
    payment_method: "",
    dp_amount: "",
    discount: "",
    notes: "",
    items: [emptyItem()],
  });

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, []);

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
  const total = Math.max(0, subtotal - Number(form.discount || 0));

  const updateItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };

  const updateMeasurement = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      measurement: { ...items[idx].measurement, [key]: val },
    };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });

  const removeItem = (idx) => {
    if (form.items.length === 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const createCustomer = async () => {
    if (!newCust.name) return toast.error("Nama customer wajib diisi");
    try {
      const { data } = await api.post("/customers", newCust);
      setCustomers([data, ...customers]);
      setForm({ ...form, customer_id: data.id });
      setShowNewCust(false);
      setNewCust({ name: "", phone: "", email: "", address: "" });
      toast.success("Customer berhasil ditambahkan");
    } catch (e) {
      toast.error("Gagal menambah customer");
    }
  };

  const submit = async () => {
    if (!form.customer_id) return toast.error("Silakan pilih customer");
    
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (it.product_type === 'Lainnya' && !it.product_name) {
        return toast.error(`Item #${i + 1}: Keterangan Lainnya wajib diisi`);
      } else if (!it.product_name) {
        return toast.error(`Item #${i + 1}: Detail / Varian wajib diisi`);
      }
      if (!it.quantity || it.quantity < 1) return toast.error(`Item #${i + 1}: Quantity wajib minimal 1`);
      if (it.unit_price === "" || Number(it.unit_price) < 0) return toast.error(`Item #${i + 1}: Harga Satuan wajib diisi`);
    }

    if (!form.payment_method || form.payment_method === "-") {
      return toast.error("Silakan pilih metode pembayaran");
    }

    const dpAmount = Number(form.dp_amount || 0);
    let payment_status = "belum";
    if (dpAmount >= total && total > 0) payment_status = "lunas";
    else if (dpAmount > 0) payment_status = "dp";
    else if (total === 0) payment_status = "lunas";

    const payload = {
      ...form,
      target_selesai: form.target_selesai
        ? new Date(form.target_selesai).toISOString() : null,
      payment_status,
      dp_amount: dpAmount,
      discount: Number(form.discount || 0),
      items: form.items.map((it) => ({
        ...it,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price),
        measurement: Object.fromEntries(
          Object.entries(it.measurement).map(([k, v]) =>
            [k, k === "untuk_siapa" ? v : Number(v || 0)]
          )
        ),
      })),
    };

    try {
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_number} berhasil dibuat`);
      nav(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat order");
    }
  };

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]" data-testid="order-form-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <Link to="/orders" className="text-xs text-stone-500 hover:text-[#A63D30] flex items-center gap-1 mb-3">
            <ArrowLeft className="h-3 w-3" /> Kembali ke daftar
          </Link>
          <div className="clf-label mb-2">Buat Baru</div>
          <h1 className="text-3xl tracking-tight font-medium">Order Baru</h1>
        </div>
      </header>

      {/* Customer */}
      <section className="bg-white border border-[#E5DCC7] p-6 space-y-4">
        <h2 className="text-lg font-medium">Customer</h2>
        {!showNewCust ? (
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="clf-label block mb-2">Pilih Customer *</label>
              <select
                data-testid="customer-select"
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-white border border-[#D9CDB5] focus:border-stone-900 rounded-sm focus:outline-none"
              >
                <option value="">— Pilih Customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ""}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setShowNewCust(true)}
              data-testid="add-customer-btn"
              className="border border-[#D9CDB5] px-4 py-2.5 text-sm font-medium hover:bg-stone-100 flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.5} /> Customer Baru
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-[#E5DCC7] p-4 bg-stone-50">
            <div>
              <label className="clf-label block mb-1">Nama *</label>
              <input data-testid="new-cust-name" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value.replace(/(^\w|\s\w)/g, m => m.toUpperCase()) })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Telp / WA</label>
              <input data-testid="new-cust-phone" inputMode="numeric" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Email</label>
              <input data-testid="new-cust-email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Alamat</label>
              <input data-testid="new-cust-address" value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button onClick={() => setShowNewCust(false)} className="px-4 py-2 text-sm border border-[#D9CDB5] hover:bg-stone-100">Batal</button>
              <button onClick={createCustomer} data-testid="save-new-cust-btn"
                className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345]">Simpan Customer</button>
            </div>
          </div>
        )}
      </section>

      {/* Items */}
      <section className="bg-white border border-[#E5DCC7] p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Item Pesanan</h2>
          <button onClick={addItem} data-testid="add-item-btn"
            className="text-sm text-[#A63D30] hover:text-[#8C3125] flex items-center gap-1 font-medium">
            <Plus className="h-4 w-4" strokeWidth={2} /> Tambah Item
          </button>
        </div>

        {form.items.map((it, idx) => (
          <div key={idx} className="border border-[#E5DCC7] p-5 bg-stone-50/40 space-y-4" data-testid={`item-card-${idx}`}>
            <div className="flex items-center justify-between">
              <div className="clf-label">Item #{idx + 1}</div>
              {form.items.length > 1 && (
                <button onClick={() => removeItem(idx)} className="text-stone-400 hover:text-red-600" data-testid={`remove-item-${idx}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              )}
            </div>

            {/* Product radio cards */}
            <div>
              <label className="clf-label block mb-2">Jenis Produk</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PRODUCT_TYPES.map((p) => (
                  <button
                    key={p} type="button"
                    onClick={() => updateItem(idx, { product_type: p })}
                    data-testid={`product-${idx}-${p.toLowerCase()}`}
                    className={`border px-4 py-3 text-sm font-medium transition-colors ${
                      it.product_type === p
                        ? "bg-[#16202B] text-white border-[#16202B]"
                        : "bg-white border-[#D9CDB5] text-stone-700 hover:border-stone-500"
                    }`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="clf-label block mb-1">
                  {it.product_type === 'Lainnya' ? 'Keterangan Lainnya *' : 'Detail / Varian *'}
                </label>
                <input data-testid={`item-name-${idx}`} value={it.product_name}
                  onChange={(e) => updateItem(idx, { product_name: e.target.value })}
                  placeholder={it.product_type === 'Lainnya' ? "Ketik keterangan produk..." : "e.g. Kemeja Lengan Panjang Putih"}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="clf-label block mb-1">Qty *</label>
                <input data-testid={`item-qty-${idx}`} type="text" inputMode="numeric" value={it.quantity}
                  onChange={(e) => updateItem(idx, { quantity: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-3 py-2 text-sm font-mono bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="clf-label block mb-1">Harga Satuan *</label>
                <div className="flex items-center bg-white border border-[#D9CDB5] focus-within:border-stone-900 rounded-sm">
                  <span className="px-3 text-xs text-stone-500 border-r border-[#E5DCC7]">Rp</span>
                  <input data-testid={`item-price-${idx}`} type="text" inputMode="numeric" value={it.unit_price ? Number(it.unit_price).toLocaleString('id-ID') : ''}
                    onChange={(e) => updateItem(idx, { unit_price: e.target.value.replace(/\D/g, '') })}
                    className="flex-1 px-3 py-2 text-sm font-mono focus:outline-none" />
                </div>
              </div>
            </div>

            {/* Measurements */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-3">
                <label className="clf-label">Ukuran Tubuh</label>
                <div className="flex items-center gap-2">
                  <span className="clf-label">Untuk:</span>
                  <input data-testid={`untuk-siapa-${idx}`} value={it.measurement.untuk_siapa}
                    onChange={(e) => updateMeasurement(idx, "untuk_siapa", e.target.value)}
                    placeholder="Umum / Nama"
                    className="px-2 py-1 text-xs font-mono bg-white border border-[#D9CDB5] rounded-sm w-40 focus:outline-none focus:border-stone-900" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 bg-white border border-[#E5DCC7] p-5">
                {MEAS_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="clf-label block mb-1">{f.label}</label>
                    <div className="flex items-baseline gap-1">
                      <input
                        data-testid={`meas-${idx}-${f.key}`}
                        type="text"
                        inputMode="decimal"
                        value={it.measurement[f.key] === 0 ? '' : it.measurement[f.key]}
                        onChange={(e) => updateMeasurement(idx, f.key, e.target.value.replace(/[^0-9.]/g, ''))}
                        className="meas-input flex-1 bg-transparent focus:outline-none w-full"
                        placeholder="0"
                      />
                      <span className="text-xs text-stone-400 font-mono">cm</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="clf-label block mb-1">Catatan Item</label>
              <input data-testid={`item-notes-${idx}`} value={it.notes}
                onChange={(e) => updateItem(idx, { notes: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
        ))}
      </section>

      {/* Payment & Dates */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5DCC7] p-6 space-y-4">
          <h2 className="text-lg font-medium">Pembayaran</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="clf-label block mb-1">Metode *</label>
              <select data-testid="payment-method" value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900">
                <option value="">— Pilih Metode —</option>
                <option value="cash">Cash</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>
            <div>
              <label className="clf-label block mb-1">Diskon (Rp)</label>
              <input data-testid="discount" type="text" inputMode="numeric" value={form.discount ? Number(form.discount).toLocaleString('id-ID') : ''}
                onChange={(e) => setForm({ ...form, discount: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 text-sm font-mono bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div className="col-span-2">
              <label className="clf-label block mb-1">DP / Bayar (Rp)</label>
              <input data-testid="dp-amount" type="text" inputMode="numeric" value={form.dp_amount ? Number(form.dp_amount).toLocaleString('id-ID') : ''}
                onChange={(e) => setForm({ ...form, dp_amount: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 text-sm font-mono bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5DCC7] p-6 space-y-4">
          <h2 className="text-lg font-medium">Tenggat & Ringkasan</h2>
          <div>
            <label className="clf-label block mb-1">Target Selesai</label>
            <input data-testid="target-selesai" type="date" value={form.target_selesai}
              onChange={(e) => setForm({ ...form, target_selesai: e.target.value })}
              className="w-full px-3 py-2 text-sm font-mono bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <label className="clf-label block mb-1">Catatan Order</label>
            <textarea data-testid="order-notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div className="pt-3 border-t border-[#E5DCC7] space-y-2">
            <div className="flex justify-between text-sm text-stone-600">
              <span>Subtotal</span><span className="font-mono">{formatRupiah(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-stone-600">
              <span>Diskon</span><span className="font-mono">- {formatRupiah(form.discount)}</span>
            </div>
            <div className="flex justify-between text-lg font-medium pt-2 border-t border-[#E5DCC7]">
              <span>Total</span>
              <span className="font-mono" data-testid="order-total">{formatRupiah(total)}</span>
            </div>
            <div className="flex justify-between text-sm font-medium pt-1 text-emerald-700">
              <span>Telah Dibayar (DP)</span>
              <span className="font-mono">{formatRupiah(form.dp_amount || 0)}</span>
            </div>
            <div className="flex justify-between text-sm font-medium pt-1 text-[#A63D30]">
              <span>Sisa Tagihan</span>
              <span className="font-mono">{formatRupiah(Math.max(0, total - Number(form.dp_amount || 0)))}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <Link to="/orders" className="px-5 py-2.5 text-sm border border-[#D9CDB5] hover:bg-stone-100">Batal</Link>
        <button onClick={submit} data-testid="submit-order-btn"
          className="bg-[#A63D30] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#8C3125]">
          Simpan Order
        </button>
      </div>
    </div>
  );
}
